import { leftDrawerOpenAtom } from '@/global-store';
import routes from '@/routes';
import LoadingSpinner from '@components/LoadingSpinner';
// import Breadcrumbs from './Breadcrumbs';
import { sidebarWidth } from '@helpers/constants';
import useWindowSize from '@hooks/useWindowSize';
import { AppBar, Box, Toolbar } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import 'overlayscrollbars/overlayscrollbars.css';
import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigation } from 'react-router-dom';
import Footer from './Footer';
import Header from './Header';
import Sidebar from './Sidebar';
//import UserTour from '@components/UserTour.jsx';

// styles
const Main = styled('main', { shouldForwardProp: prop => prop !== 'open' })(({ theme, open }) => ({
    backgroundColor: '#f1f1f1',
    width: '100%',
    maxWidth: `calc(100% - (${sidebarWidth}px + 32px))`,
    minHeight: 'calc(100vh - 68px)',
    // minHeight: 'calc(100dvh - 68px)',
    flexGrow: 1,
    padding: '32px',
    marginRight: '32px',
    marginTop: '65px',
    borderRadius: theme.borders.borderRadius.xl,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,

    [theme.breakpoints.down('xxl')]: {
        marginRight: '16px',
        maxWidth: `calc(100% - (${sidebarWidth}px + 16px))`
    },

    [theme.breakpoints.down('xl')]: {
        padding: '26px',
        marginRight: 0,
        maxWidth: '100%'
    },

    [theme.breakpoints.down('sm')]: {
        padding: '20px'
    },

    ...(!open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        })
    }),

    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        })
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //
function MainLayout() {
    const theme = useTheme();
    const location = useLocation();

    /*const steps = [
    {
      content: t('dashboardGuideHomeWelcome'),
      title: t('dashboardGuideTitle'),
      placement: 'center',
      target: 'body',
      disableBeacon: true
    },
    {
      target: '#header-right-section',
      content: t('dashboardGuideHomeStep1'),
      disableBeacon: true
    },
    {
      target: '#collapse-assist',
      content: t('dashboardGuideHomeStep3'),
      disableBeacon: true
    },
    {
      target: '#route-audit',
      content: t('dashboardGuideHomeStep4'),
      disableBeacon: true
    },
    {
      target: '#list-subheader-report',
      content: t('dashboardGuideHomeStep5'),
      disableBeacon: true
    },
    {
      target: '#collapse-report-analysis',
      content: t('dashboardGuideHomeStep6'),
      disableBeacon: true
    },
    {
      target: '#list-subheader-tools',
      content: t('dashboardGuideHomeStep7'),
      disableBeacon: true
    },
    {
      target: '#support-popup',
      content: t('dashboardGuideHomeStep8'),
      disableBeacon: true
    }
  ];*/

    const windowSize = useWindowSize();

    const navigation = useNavigation();

    // Handle left drawer
    const [leftDrawerOpen, setLeftDrawerOpen] = useAtom(leftDrawerOpenAtom);

    const handleLeftDrawerToggle = () => {
        setLeftDrawerOpen(!leftDrawerOpen);
    };

    const appNavbar = useRef(null);

    useEffect(() => {
        function handleTransparentNavbar() {
            //const appNavbar = document.getElementById('AppNavbar');
            if (!appNavbar?.current) return;
            if (window.scrollY < 5) {
                appNavbar.current.style.background = 'none';
                appNavbar.current.style['backdrop-filter'] = 'none';
            } else {
                appNavbar.current.style.background = 'rgba(0,0,0, 0.85)';
                appNavbar.current.style['backdrop-filter'] = 'blur(5px)';
            }
        }

        /**
         The event listener that's calling the handleTransparentNavbar function when
         scrolling the window.
         */
        window.addEventListener('scroll', handleTransparentNavbar);

        // Call the handleTransparentNavbar function to set the state with the initial value.
        handleTransparentNavbar();

        return () => window.removeEventListener('scroll', handleTransparentNavbar);
    }, []);

    useEffect(() => {
        function sidenavEventCallback() {
            const xlAndUp = window.innerWidth >= theme.breakpoints.values.xl;
            setLeftDrawerOpen(xlAndUp);
        }
        sidenavEventCallback();
    }, [location.pathname, windowSize.width, setLeftDrawerOpen, theme.breakpoints.values.xl]);

    return (
        <Box sx={{ display: 'flex' }}>
            {/* header */}
            <AppBar
                elevation={0}
                enableColorOnDark
                id='AppNavbar'
                position='fixed'
                ref={appNavbar}
                sx={{
                    background: 'transparent',
                    color: theme.palette.primary.white,
                    transition: leftDrawerOpen ? theme.transitions.create('width') : 'none',
                    p: 0
                }}
            >
                <Toolbar
                    sx={{
                        'minHeight': '64px',
                        'background': 'transparent',
                        'pl': {
                            xs: 2 + '!important',
                            md: 3 + '!important'
                        },
                        'pr': {
                            xs: 2 + '!important',
                            md: 3 + '!important',
                            lg: '16px !important',
                            xl: '32px !important'
                        },

                        '@media (max-width: 270px)': {
                            px: 1.5 + '!important'
                        }
                    }}
                >
                    <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
                </Toolbar>
            </AppBar>

            {/* drawer */}
            <Sidebar
                drawerOpen={leftDrawerOpen}
                drawerToggle={handleLeftDrawerToggle}
                routes={routes[0].children}
            />

            {/* main content */}
            <Main id='main-content' open={leftDrawerOpen} theme={theme}>
                <Box
                    sx={({ breakpoints }) => ({
                        margin: '0 auto',
                        maxWidth: breakpoints.values.xxl + 'px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%'
                    })}
                >
                    {/* <Breadcrumbs /> */}

                    {navigation.state === 'loading' ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            <Outlet />

                            {/*<UserTour steps={steps} isIntroTour />*/}

                            <Footer />
                        </>
                    )}
                </Box>
            </Main>
        </Box>
    );
}

export default MainLayout;
